import React, { useMemo, useState } from 'react'
import { ReactComponent as AttendanceIcon } from '@assets/images/Navigation/attendance.svg'
import { ReactComponent as BillingIcon } from '@assets/images/Navigation/billing.svg'
import { ReactComponent as CampaignIcon } from '@assets/images/Navigation/campaign.svg'
import { ReactComponent as ChartingIcon } from '@assets/images/Navigation/charting.svg'
import { Icon as ClaimsIcon } from '@enamelai/design-system/icons/Insurance_plus'
import { ReactComponent as DashboardIcon } from '@assets/images/Navigation/dashboard.svg'
import { ReactComponent as FilesIcon } from '@assets/images/Navigation/files-letters.svg'
import { ReactComponent as FormBuilderIcon } from '@assets/images/Navigation/form-builder.svg'
import { ReactComponent as ImageViewerIcon } from '@assets/images/Navigation/image-viewer.svg'
import { Icon as InsuranceIcon } from '@enamelai/design-system/icons/Insurance_module'
import { ReactComponent as LabOrderIcon } from '@assets/images/Navigation/lab-order.svg'
import { ReactComponent as MembershipIcon } from '@assets/images/Navigation/membership.svg'
import { ReactComponent as CommunicationsIcon } from '@assets/images/Navigation/communications.svg'
import { ReactComponent as NotesIcon } from '@assets/images/Navigation/notes.svg'
import { ReactComponent as PatientsIcon } from '@assets/images/Navigation/patients.svg'
import { ReactComponent as PerformanceIcon } from '@assets/images/Navigation/avatar-clock.svg'
import { ReactComponent as StaffIcon } from '@assets/images/Navigation/staff.svg'
import { ReactComponent as ReportingIcon } from '@assets/images/Navigation/reporting.svg'
import { ReactComponent as ScheduleIcon } from '@assets/images/Navigation/scheduler.svg'
import { ReactComponent as SettingsIcon } from '@assets/images/Navigation/settings.svg'
import { Drawer, type DrawerItem } from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'

const NAV_ITEMS: DrawerItem[] = [
  {
    id: 'attendance',
    label: 'Attendance',
    icon: <AttendanceIcon />,
  },
  {
    id: 'performance',
    label: 'Performance',
    icon: <PerformanceIcon />,
  },
  {
    id: 'schedule',
    label: 'Appointment',
    icon: <ScheduleIcon />,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
  },
  {
    id: 'patients',
    label: 'Patients',
    icon: <PatientsIcon />,
  },
  {
    id: 'staff',
    label: 'Staff',
    icon: <StaffIcon />,
  },
  {
    id: 'messaging',
    label: 'Messaging',
    icon: <CommunicationsIcon />,
  },
  {
    id: 'insurance',
    label: 'Insurance',
    icon: <InsuranceIcon />,
  },
  { id: 'notes', label: 'Notes', icon: <NotesIcon /> },
  {
    id: 'files',
    label: 'Files',
    icon: <FilesIcon />,
  },
  {
    id: 'charting',
    label: 'Charting',
    icon: <ChartingIcon />,
  },
  {
    id: 'image-viewer',
    label: 'Image viewer',
    icon: <ImageViewerIcon />,
  },
  {
    id: 'claims',
    label: 'Claims',
    icon: <ClaimsIcon />,
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: <BillingIcon />,
  },
  {
    id: 'reporting',
    label: 'Reporting',
    icon: <ReportingIcon />,
  },
  {
    id: 'campaign',
    label: 'Marketing',
    icon: <CampaignIcon />,
  },
  {
    id: 'form-builder',
    label: 'Form builder',
    icon: <FormBuilderIcon />,
  },
  {
    id: 'lab-order',
    label: 'Lab order',
    icon: <LabOrderIcon />,
  },
  {
    id: 'membership',
    label: 'Membership',
    icon: <MembershipIcon />,
  },
]

const FOOTER_ITEMS: DrawerItem[] = [
  {
    id: 'settings',
    label: 'Settings',
    icon: <SettingsIcon />,
  },
]

const DrawerShowcase: React.FC = () => {
  const [expanded, setExpanded] = useState(true)
  const [activeId, setActiveId] = useState('dashboard')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'drawer-expanded',
            label: 'Expanded',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="drawer-expanded"
                  type="checkbox"
                  checked={expanded}
                  onChange={(event) => setExpanded(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'drawer-active-item',
            label: 'Active item',
            control: (
              <select
                id="drawer-active-item"
                className="ui-field-control"
                value={activeId}
                onChange={(event) => setActiveId(event.target.value)}
              >
                {NAV_ITEMS.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
    ],
    [activeId, expanded],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Drawer"
        description="Collapsible navigation rail with icon-only and expanded modes, active and hover states, in light and color modes."
        stageClassName="ui-preview__stage--wide drawer-showcase__stage-wrap"
      >
        <div className="drawer-showcase__sheet">
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Toggle the chevron to expand or collapse, and select an item to
              set the active state.
            </p>
            <div className="drawer-showcase__stage">
              <Drawer
                items={NAV_ITEMS}
                footerItems={FOOTER_ITEMS}
                activeId={activeId}
                onSelect={setActiveId}
                expanded={expanded}
                onExpandedChange={setExpanded}
              />
            </div>
          </section>

          <div className="drawer-showcase__section-header">
            <span className="drawer-showcase__section-label">Variants</span>
          </div>

          <div className="drawer-showcase__variants">
            <div className="drawer-showcase__variant">
              <span className="drawer-showcase__variant-label">
                Default · Light
              </span>
              <div className="drawer-showcase__stage">
                <Drawer
                  items={NAV_ITEMS.slice(0, 8)}
                  footerItems={FOOTER_ITEMS}
                  activeId="dashboard"
                  previewState="default"
                />
              </div>
            </div>

            <div className="drawer-showcase__variant">
              <span className="drawer-showcase__variant-label">
                Hovered · Light
              </span>
              <div className="drawer-showcase__stage">
                <Drawer
                  items={NAV_ITEMS.slice(0, 8)}
                  footerItems={FOOTER_ITEMS}
                  activeId="settings"
                  previewState="hover"
                />
              </div>
            </div>

            <div className="drawer-showcase__variant">
              <span className="drawer-showcase__variant-label">
                Expanded · Light
              </span>
              <div className="drawer-showcase__stage">
                <Drawer
                  items={NAV_ITEMS.slice(0, 8)}
                  footerItems={FOOTER_ITEMS}
                  activeId="dashboard"
                  expanded
                  previewState="default"
                />
              </div>
            </div>

            <div className="drawer-showcase__variant">
              <span className="drawer-showcase__variant-label">
                Default · Dark
              </span>
              <div className="drawer-showcase__stage">
                <Drawer
                  items={NAV_ITEMS.slice(0, 8)}
                  footerItems={FOOTER_ITEMS}
                  activeId="dashboard"
                  previewState="default"
                />
              </div>
            </div>

            <div className="drawer-showcase__variant">
              <span className="drawer-showcase__variant-label">
                Hovered · Dark
              </span>
              <div className="drawer-showcase__stage">
                <Drawer
                  items={NAV_ITEMS.slice(0, 8)}
                  footerItems={FOOTER_ITEMS}
                  activeId="settings"
                  previewState="hover"
                />
              </div>
            </div>

            <div className="drawer-showcase__variant">
              <span className="drawer-showcase__variant-label">
                Expanded · Dark
              </span>
              <div className="drawer-showcase__stage">
                <Drawer
                  items={NAV_ITEMS.slice(0, 8)}
                  footerItems={FOOTER_ITEMS}
                  activeId="dashboard"
                  expanded
                  previewState="default"
                />
              </div>
            </div>
          </div>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Drawer" sections={propertySections} />
    </>
  )
}

export default DrawerShowcase
